import { createReducer } from "typesafe-actions";
import {
  selectShip,
  selectVoyage,
  loadPositionsSuccess,
  setSelectedYear,
} from "./voyagesData.actions";
import { toggleHighlight } from "../uiControl/uiControl.actions";
import {
  Voyage,
  Entry,
  Position,
  RawPosition,
} from "../../services/types";
import { nanoid } from "nanoid";

export interface PositionDataState {
  isPositionDataLoading: boolean;
  isShipDataLoading: boolean;
  selectedVoyageData?: Voyage;
  selectedVoyagePostitions: Position[];
  voyagesForSelectedShip: Voyage[][];
  selectedVoyageEntries: Entry[];
  highlightedEntries: boolean[];
  selectedYear: string;
}

export const positionDataState: PositionDataState = {
  isPositionDataLoading: false,
  isShipDataLoading: false,
  selectedVoyageData: undefined,
  selectedVoyagePostitions: [],
  voyagesForSelectedShip: [],
  selectedVoyageEntries: [],
  highlightedEntries: [],
  selectedYear: "",
};

export default createReducer<PositionDataState>(positionDataState)
  .handleAction(selectVoyage.request, (state, action) => {
    let selectedVoyage;
    let yearIndex;
    for (
      yearIndex = 0;
      yearIndex < state.voyagesForSelectedShip.length;
      yearIndex++
    ) {
      selectedVoyage = state.voyagesForSelectedShip[yearIndex].find(
        (voyage) => voyage.id === action.payload
      );
      if (selectedVoyage) {
        break;
      }
    }

    return {
      ...state,
      isPositionDataLoading: true,
      selectedVoyageData: selectedVoyage,
    };
  })
  .handleAction(loadPositionsSuccess, (state, action) => {
    const combinedPositions =
      action.payload.anchorData[0]?.data?.positions ?? [];

    const augmentedLocationData = action.payload.locationData.map(
      (position: RawPosition) => ({
        timestamp: +position.utcTime,
        lat: +position.latitude,
        long: +position.longitude,
        offset: +position.timeZoneOffset,
      })
    );

    combinedPositions.push(...augmentedLocationData);

    return {
      ...state,
      selectedVoyagePostitions: combinedPositions
        .map((position: Position) => ({
          ...position,
          id: nanoid(),
        })).sort(
          (a: Position, b: Position) => (b.timestamp + b.offset) - (a.timestamp + a.offset)
        ),
    };
  })
  .handleAction(selectVoyage.success, (state, action) => {
    if (!state.selectedVoyagePostitions[0]) {
      return {
        ...state,
        isPositionDataLoading: false,
        selectedVoyageEntries: [],
      };
    }
    const entries = action.payload.map((entry) => {
      const augmentedEntry = { ...entry };
      augmentedEntry.data.timeStamp = Date.parse(
        entry.data.date + "T" + entry.data.time
      );

      //Figures out where on the map to put the blog entry based on what time it was logged
      let entryPosition = state.selectedVoyagePostitions.reduce(
        (closestTimeSoFar, position) => {
          position.timeDifference = Math.abs(
            position.timestamp +
            position.offset -
            augmentedEntry.data.timeStamp! /*Time stampDefined Above*/
          );

          if (
            position.timeDifference <
            closestTimeSoFar.timeDifference! /*Defined on reduce inital value*/
          ) {
            return position;
          } else {
            return closestTimeSoFar;
          }
        },
        {
          id: "",
          timestamp: 0,
          lat: 0,
          long: 0,
          offset: 0,
          timeDifference: 99999999999999,
        }
      );

      //Should never be called but is still needed for safety
      if (!entryPosition) {
        entryPosition =
          state.selectedVoyagePostitions[
            state.selectedVoyagePostitions.length - 1
          ];
      }
      augmentedEntry.lat = entryPosition.lat;
      augmentedEntry.long = entryPosition.long;
      return augmentedEntry;
    });

    return {
      ...state,
      isPositionDataLoading: false,
      selectedVoyageEntries: entries,
      highlightedEntries: new Array(entries.length).fill(false),
    };
  })
  .handleAction(selectVoyage.failure, (state, action) => {
    return {
      ...state,
      isPositionDataLoading: false,
      selectedVoyageData: undefined,
      selectedVoyageEntries: [],
      selectedVoyagePostitions: [],
    };
  })
  .handleAction([selectShip.request], (state, action) => {
    return {
      ...state,
      isShipDataLoading: true,
    };
  })
  .handleAction([selectShip.success], (state, action) => {
    const voyagesForSelectedShip: Voyage[][] = [];
    let yearIndex = 0;

    //Puts voyages in an arrays by year
    action.payload.forEach((voyage, index: number) => {
      if (index === 0) {
        voyagesForSelectedShip[yearIndex] = [voyage];
        return;
      }

      if (
        voyage.data.dateStart.substring(0, 4) ===
        voyagesForSelectedShip[yearIndex][0].data.dateStart.substring(0, 4)
      ) {
        voyagesForSelectedShip[yearIndex].push(voyage);
      } else {
        yearIndex++;
        voyagesForSelectedShip[yearIndex] = [voyage];
      }
    });

    return {
      ...state,
      voyagesForSelectedShip: voyagesForSelectedShip,
      isShipDataLoading: false,
    };
  })
  .handleAction([selectShip.failure], (state, action) => {
    return {
      ...state,
      isShipDataLoading: false,
      voyagesForSelectedShip: [],
      selectedVoyageData: undefined,
      selectedVoyageEntries: [],
      selectedVoyagePostitions: [],
    };
  })
  .handleAction(toggleHighlight, (state, action) => {
    const index = action.payload;
    const entries = [...state.highlightedEntries];
    entries[index] = !state.highlightedEntries[index];
    return {
      ...state,
      highlightedEntries: entries,
    };
  })
  .handleAction(setSelectedYear, (state, action) => {
    return {
      ...state,
      selectedYear: action.payload,
    };
  });
